import { supabase } from './supabase';

/*
  The two numbers Site Settings has always saved and nothing has ever read.

  Written 5 September 2026, on the owner's decision, for "Settle the settings
  that save and do nothing". Both boxes saved correctly and **no alert of either
  kind existed anywhere in OnHand**, so an administrator who set a threshold
  reasonably believed something would happen.

  A rule that is not set is not a rule. A blank box means no warning at all,
  which is why these come back as null rather than as a zero: a threshold of zero
  would flag every single line.
*/

export const VARIANCE_KEY   = 'variance_alert_threshold_pct';
export const NOT_STARTED_KEY = 'not_started_alert_days_before_close';

export async function countRules() {
  const { data, error } = await supabase
    .from('app_settings').select('key, value').in('key', [VARIANCE_KEY, NOT_STARTED_KEY]);
  if (error) return { variancePct: null, notStartedDays: null };
  const bag = {};
  for (const r of data || []) bag[r.key] = r.value;
  const num = v => {
    const n = Number(String(v ?? '').trim());
    return Number.isFinite(n) && n > 0 ? n : null;
  };
  return { variancePct: num(bag[VARIANCE_KEY]), notStartedDays: num(bag[NOT_STARTED_KEY]) };
}

/*
  Which lines on a sheet changed by more than the threshold since last quarter.

  **A line with nothing to compare against is not a variance**, and that is the
  case worth being careful about: a brand new item has no previous quantity, and
  calling that "up by an infinite amount" would flag every new product in the
  catalog on the first count that carries it. Rule 9's habit again, one table
  along: a blank is not a zero.

  **Nor is a line that went from nothing to something.** Somebody stocked a new
  item. That is news, but it is not the same news, and this box asks about
  quantities moving.
*/
export function bigChanges(lines, thresholdPct) {
  if (!thresholdPct) return [];
  const out = [];
  for (const l of lines || []) {
    const before = Number(l.previous_quantity);
    const after  = Number(l.quantity);
    if (!Number.isFinite(before) || before <= 0) continue;
    if (!Number.isFinite(after)) continue;
    const pct = Math.abs(after - before) / before * 100;
    if (pct > thresholdPct) {
      out.push({
        item: l.item_number_raw || l.description_raw || 'an item',
        before, after, pct: Math.round(pct),
      });
    }
  }
  return out;
}
